import {
  BadgeIcon,
  ClockIcon,
  HashIcon,
  RefreshCwIcon,
  TagIcon,
  TruckIcon,
  UsersIcon,
} from 'lucide-react';
import { useParams } from 'react-router';

import { Alert, AlertDescription, AlertTitle } from '@/components/core/alert';
import { Avatar, AvatarFallback } from '@/components/core/avatar';
import { Button } from '@/components/core/button';
import { DetailGroup } from '@/components/detail-group';
import { DetailList } from '@/components/detail-list';
import { Header, HeaderActions, HeaderIcon, HeaderTitle } from '@/components/header';
import { DetailMain, DetailSide, Page, PageContent } from '@/components/layout';
import { carrierInitials } from '@/pages/platform/carriers/carrier-initials';
import { formatDateTime } from '@/shared/helpers';
import { costingStrategyTitle } from '@/shared/title-maps';

import { CarrierSummaryCard } from './carrier-summary-card';
import { useCarrierAccount } from './use-carrier-account';
import { WeightBracketsCard } from './weight-brackets-card';

/**
 * Carrier-account detail page: the account identity (read-only), its carrier summary, and its
 * weight-bracket configs. Creating an account is a dialog on the list page, so this route is only
 * ever an existing account.
 */
export function CarrierAccountDetails() {
  const { id } = useParams();
  const {
    carrierAccount,
    carrier,
    weightBrackets,
    isLoadingAccount,
    isLoadingBrackets,
    hasError,
    refetchBrackets,
    saveBrackets,
  } = useCarrierAccount(id!);

  return (
    <Page>
      <Header loading={isLoadingAccount}>
        <HeaderIcon parentUrl="/shipping/carrier-accounts" parentTitle="Carrier accounts">
          <TruckIcon />
        </HeaderIcon>
        <HeaderTitle>{carrierAccount?.name ?? 'Carrier account'}</HeaderTitle>
        {!hasError && (
          <HeaderActions>
            <Button
              size="icon"
              variant="outline"
              aria-label="Refresh"
              onClick={() => void refetchBrackets()}
              disabled={isLoadingBrackets}
            >
              <RefreshCwIcon />
            </Button>
          </HeaderActions>
        )}
      </Header>
      {hasError ? (
        <PageContent variant="column">
          <div className="mx-auto w-full max-w-3xl">
            <Alert variant="error">
              <AlertTitle>Couldn&apos;t load this carrier account</AlertTitle>
              <AlertDescription>
                We couldn&apos;t load this carrier account or its weight brackets. Go back to the
                carrier accounts list and try again.
              </AlertDescription>
            </Alert>
          </div>
        </PageContent>
      ) : (
        <PageContent variant="split">
          <DetailMain>
            <CarrierSummaryCard
              carrierId={carrierAccount?.carrierId}
              carrierName={carrierAccount?.carrierName}
              baseTrackingUrl={carrier?.baseTrackingUrl}
              isLoading={isLoadingAccount}
            />
            <WeightBracketsCard
              carrierId={carrierAccount?.carrierId}
              weightBrackets={weightBrackets}
              isLoading={isLoadingBrackets}
              saveBrackets={saveBrackets}
            />
          </DetailMain>
          <DetailSide>
            <DetailGroup>
              <DetailList
                title="Details"
                isLoading={isLoadingAccount}
                items={[
                  { icon: TagIcon, label: 'Name', value: carrierAccount?.name },
                  { icon: HashIcon, label: 'Code', value: carrierAccount?.code },
                  {
                    icon: TruckIcon,
                    label: 'Carrier',
                    value: carrierAccount ? (
                      <div className="flex min-w-0 items-center gap-2">
                        <Avatar size="sm">
                          <AvatarFallback>
                            {carrierInitials(carrierAccount.carrierName)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="min-w-0 truncate">{carrierAccount.carrierName}</span>
                      </div>
                    ) : undefined,
                  },
                  { icon: UsersIcon, label: 'Account owner', value: carrierAccount?.accountName },
                  {
                    icon: BadgeIcon,
                    label: 'Costing strategy',
                    value: carrierAccount
                      ? costingStrategyTitle[carrierAccount.costingStrategy]
                      : undefined,
                  },
                  {
                    icon: ClockIcon,
                    label: 'Updated',
                    value: carrierAccount ? formatDateTime(carrierAccount.updatedAt) : undefined,
                  },
                ]}
              />
            </DetailGroup>
          </DetailSide>
        </PageContent>
      )}
    </Page>
  );
}
